import React, { useState, useEffect } from 'react';
import { getFullImageUrl, handleImageError, PLACEHOLDER_IMAGE } from '../utils/imageUtils';

interface ToastMessage {
  id: number;
  message: string;
  type: 'success' | 'error';
}

const Toast = ({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg text-white font-medium z-50 animate-slide-in ${
      type === 'success' ? 'bg-green-600' : 'bg-red-600'
    }`}>
      <div className="flex items-center gap-2">
        <span>{type === 'success' ? '✅' : '❌'}</span>
        <span>{message}</span>
        <button 
          onClick={onClose}
          className="ml-2 text-white hover:text-gray-200"
        >
          ×
        </button>
      </div>
    </div>
  );
};

interface TrustedTeam {
  _id: string;
  name: string;
  description: string;
  logoUrl: string;
  website: string;
  category?: {
    _id: string;
    name: string;
  } | null;
  featured: boolean;
  displayOrder: number;
  isActive: boolean;
}

interface FormData {
  name: string;
  description: string;
  logoUrl: string;
  website: string;
  category?: string;
  featured: boolean;
  displayOrder: number;
}

interface ResourceCategory {
  _id: string;
  name: string;
  icon: string;
  featured?: boolean;
}

interface TrustedByTeamsProps {
  token?: string;
}

const TrustedByTeams: React.FC<TrustedByTeamsProps> = ({ token }) => {
  const [teams, setTeams] = useState<TrustedTeam[]>([]);
  const [categories, setCategories] = useState<ResourceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState<TrustedTeam | null>(null);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    logoUrl: '',
    website: '',
    category: '',
    featured: false,
    displayOrder: 0
  });

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryData, setNewCategoryData] = useState({
    name: '',
    icon: '📦',
    featured: false
  });
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const filteredTeams = teams.filter(team =>
    team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    team.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    fetchTeams();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/resource-categories');
      if (response.ok) {
        const result = await response.json();
        const categoriesList = result.success ? result.data : result;
        setCategories(Array.isArray(categoriesList) ? categoriesList : []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchTeams = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/trusted-teams');
      
      if (response.ok) {
        const result = await response.json();
        const teamsList = result.success ? result.data : result;
        setTeams(Array.isArray(teamsList) ? teamsList : []);
      } else {
        setError(`Failed to fetch teams: ${response.status}`);
        setTeams([]);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unknown error occurred');
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      
      const response = await fetch(`${apiUrl}/api/upload/blog-image`, {
        method: 'POST',
        credentials: 'include',
        body: uploadFormData
      });

      const data = await response.json();
      if (data.success) {
        setFormData(prev => ({
          ...prev,
          logoUrl: data.url
        }));
        showToast('Image uploaded successfully!', 'success');
      } else {
        showToast(`Upload failed: ${data.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      showToast('Error uploading image', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (team: TrustedTeam) => {
    setEditingTeam(team);
    setFormData({
      name: team.name,
      description: team.description,
      logoUrl: team.logoUrl,
      website: team.website,
      category: team.category?._id || '',
      featured: team.featured,
      displayOrder: team.displayOrder
    });
    setShowForm(true);
  };

  const handleDelete = async (teamId: string) => {
    if (!window.confirm('Are you sure you want to delete this team?')) {
      return;
    }

    try {
      const response = await fetch(`/api/trusted-teams/${teamId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        showToast('Team deleted successfully!', 'success');
        fetchTeams();
      } else {
        showToast('Error deleting team', 'error');
      }
    } catch (error) {
      console.error('Error deleting team:', error);
      showToast('Error deleting team', 'error');
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryData.name.trim()) {
      showToast('Category name is required', 'error');
      return;
    }

    setCreatingCategory(true);
    
    try {
      const response = await fetch('/api/resource-categories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          name: newCategoryData.name,
          description: `${newCategoryData.name} resources`,
          icon: newCategoryData.icon,
          featured: newCategoryData.featured,
          displayOrder: categories.length
        })
      });

      if (response.ok) {
        const result = await response.json();
        const newCategory = result.success ? result.data : result;
        setCategories([...categories, newCategory]);
        setFormData(prev => ({
          ...prev,
          category: newCategory._id
        }));
        setShowCategoryModal(false);
        setNewCategoryData({ name: '', icon: '📦', featured: false });
        showToast('Category created successfully!', 'success');
      } else {
        showToast('Failed to create category', 'error');
      }
    } catch (error) {
      console.error('Error creating category:', error);
      showToast('Error creating category', 'error');
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategoryId) return;
    if (!newCategoryData.name.trim()) {
      showToast('Category name is required', 'error');
      return;
    }

    setCreatingCategory(true);
    
    try {
      const response = await fetch(`/api/resource-categories/${editingCategoryId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          name: newCategoryData.name,
          icon: newCategoryData.icon,
          featured: newCategoryData.featured
        })
      });

      if (response.ok) {
        const result = await response.json();
        const updatedCategory = result.success ? result.data : result;
        setCategories(categories.map(cat => cat._id === editingCategoryId ? updatedCategory : cat));
        setShowCategoryModal(false);
        setEditingCategoryId(null);
        setNewCategoryData({ name: '', icon: '📦', featured: false });
        showToast('Category updated successfully!', 'success');
      } else {
        showToast('Failed to update category', 'error');
      }
    } catch (error) {
      console.error('Error updating category:', error);
      showToast('Error updating category', 'error');
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      const response = await fetch(`/api/resource-categories/${categoryId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setCategories(categories.filter(cat => cat._id !== categoryId));
        if (formData.category === categoryId) {
          setFormData(prev => ({
            ...prev,
            category: ''
          }));
        }
        showToast('Category deleted successfully!', 'success');
      } else {
        showToast('Failed to delete category', 'error');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      showToast('Error deleting category', 'error');
    }
  };

  const handleMoveCategory = async (categoryId: string, direction: 'up' | 'down') => {
    const currentIndex = categories.findIndex(cat => cat._id === categoryId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= categories.length) return;

    const newDisplayOrder = newIndex;
    
    try {
      const response = await fetch(`/api/resource-categories/${categoryId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          name: categories[currentIndex].name,
          icon: categories[currentIndex].icon,
          featured: categories[currentIndex].featured,
          displayOrder: newDisplayOrder
        })
      });

      if (response.ok) {
        const newCategories = [...categories];
        const [movedCategory] = newCategories.splice(currentIndex, 1);
        newCategories.splice(newIndex, 0, movedCategory);
        setCategories(newCategories);
        showToast(`Category moved ${direction}!`, 'success');
      } else {
        showToast('Failed to move category', 'error');
      }
    } catch (error) {
      console.error('Error moving category:', error);
      showToast('Error moving category', 'error');
    }
  };

  const handleEditCategory = (category: ResourceCategory) => {
    setEditingCategoryId(category._id);
    setNewCategoryData({
      name: category.name,
      icon: category.icon,
      featured: category.featured || false
    });
    setShowCategoryModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const url = editingTeam
        ? `/api/trusted-teams/${editingTeam._id}`
        : '/api/trusted-teams';
      
      const method = editingTeam ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        showToast(editingTeam ? 'Team updated successfully!' : 'Team created successfully!', 'success');
        resetForm();
        fetchTeams();
      } else {
        const error = await response.json();
        showToast(error.error || 'Error saving team', 'error');
      }
    } catch (error) {
      console.error('Error saving team:', error);
      showToast('Error saving team', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      logoUrl: '',
      website: '',
      category: '',
      featured: false,
      displayOrder: 0
    });
    setEditingTeam(null);
    setShowForm(false);
  };

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Trusted By Teams</h1>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <strong>Error:</strong> {error}
          <button 
            onClick={() => fetchTeams()} 
            className="ml-4 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Trusted By Teams</h1>
        <p>Loading teams...</p>
      </div>
    );
  }

  return (
    <div className="text-gray-100 font-sans h-full" style={{backgroundColor: '#0a0908', padding: '2rem'}}>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-dusk-amber to-dusk-copper bg-clip-text text-transparent mb-2">Trusted By Teams</h1>
            <p className="text-gray-400">Manage teams and organizations that trust DUSKPROOF</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search teams..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50 focus:border-dusk-copper transition-all"
              />
              <svg 
                className="absolute left-3 top-3.5 h-5 w-5 text-[var(--dusk-faint)]" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <button
              onClick={() => {
                if (showForm) {
                  resetForm();
                } else {
                  setShowForm(true);
                }
              }}
              className="px-6 py-3 bg-gradient-to-r from-dusk-copper to-dusk-amber hover:from-dusk-copper-deep hover:to-dusk-copper text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-dusk-copper/40"
            >
              {showForm ? (
                <>
                  <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancel
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Team
                </>
              )}
            </button>
          </div>
        </div>

        {/* Team Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="p-8 space-y-6 mb-8 rounded-2xl border border-[var(--dusk-line)] bg-[rgba(243,235,226,0.04)] backdrop-blur-sm">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Team Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white h-24 resize-none"
                placeholder="Describe the team or organization..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Logo/Image
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  className="flex-1 px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                />
                {uploading && <span className="text-gray-400">Uploading...</span>}
              </div>
              {formData.logoUrl && (
                <div className="mt-4">
                  <img 
                    src={getFullImageUrl(formData.logoUrl)} 
                    alt="Team logo" 
                    className="h-20 rounded border border-[var(--dusk-line-strong)]"
                    onError={handleImageError}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Website
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                placeholder="https://example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Category
              </label>
              <div className="space-y-2">
                <select
                  value={formData.category || ''}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                >
                  <option value="">-- Select Category --</option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                
                {/* Edit/Delete/Favorite buttons for selected category */}
                {formData.category && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const selected = categories.find(c => c._id === formData.category);
                          if (selected) handleEditCategory(selected);
                        }}
                        className="text-amber-400 hover:text-amber-300 transition-colors"
                        title="Edit category"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteCategory(formData.category!)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                        title="Delete category"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
                
                <button
                  type="button"
                  onClick={() => {
                    setEditingCategoryId(null);
                    setNewCategoryData({ name: '', icon: '📦', featured: false });
                    setShowCategoryModal(true);
                  }}
                  className="w-full px-3 py-2 bg-dusk-copper hover:bg-dusk-copper-deep text-white rounded-md text-sm font-medium transition-colors"
                >
                  + Add New Category
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Display Order
              </label>
              <input
                type="number"
                value={formData.displayOrder}
                onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) })}
                className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
              />
            </div>

            <div className="flex items-end">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  className="w-4 h-4 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded accent-dusk-copper"
                />
                <span className="text-gray-300">⭐ Featured</span>
              </label>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
              >
                {editingTeam ? 'Update Team' : 'Create Team'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 bg-[rgba(243,235,226,0.06)] hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Teams Table */}
        <div className="rounded-2xl border border-[var(--dusk-line)] overflow-hidden bg-[rgba(243,235,226,0.03)] backdrop-blur-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[rgba(243,235,226,0.05)] border-b border-[var(--dusk-line)]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Team
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Website
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Featured
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/30">
                {filteredTeams.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-400">
                      No data
                    </td>
                  </tr>
                ) : (
                  filteredTeams.map((team) => (
                    <tr key={team._id} className="hover:bg-[rgba(243,235,226,0.05)] transition-all">
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center space-x-3">
                          {team.logoUrl && (
                            <img 
                              src={getFullImageUrl(team.logoUrl)} 
                              alt={team.name}
                              className="w-10 h-10 rounded object-cover"
                              onError={handleImageError}
                            />
                          )}
                          <span className="font-medium text-white">{team.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {team.description.substring(0, 50)}{team.description.length > 50 ? '...' : ''}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {team.website ? (
                          <a 
                            href={team.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-dusk-amber hover:text-dusk-amber"
                          >
                            Visit
                          </a>
                        ) : (
                          <span className="text-[var(--dusk-faint)]">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {team.category ? (
                          <span className="px-3 py-1 bg-purple-600/30 text-purple-400 rounded-full text-xs font-medium">
                            {team.category.name}
                          </span>
                        ) : (
                          <span className="text-[var(--dusk-faint)]">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-center">
                        {team.featured ? (
                          <span className="px-3 py-1 bg-yellow-600/30 text-yellow-400 rounded-full text-xs font-medium">
                            ⭐ Featured
                          </span>
                        ) : (
                          <span className="text-[var(--dusk-faint)]">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right text-sm">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(team)}
                            className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20 rounded-lg transition-all"
                            title="Edit team"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(team._id)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-all"
                            title="Delete team"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Toast Messages */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      {/* Create/Edit Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[rgba(243,235,226,0.04)] rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-white mb-4">
              {editingCategoryId ? 'Edit Category' : 'Create New Category'}
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category Name
                </label>
                <input
                  type="text"
                  value={newCategoryData.name}
                  onChange={(e) => setNewCategoryData({ ...newCategoryData, name: e.target.value })}
                  placeholder="e.g., EXCHANGES"
                  className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white placeholder-[var(--dusk-faint)]"
                />
              </div>

              {/* Move Buttons for Category */}
              {editingCategoryId && (
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => handleMoveCategory(editingCategoryId, 'up')}
                    disabled={categories.findIndex(c => c._id === editingCategoryId) === 0}
                    className="flex-1 px-3 py-2 bg-[rgba(243,235,226,0.06)] hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md transition-colors flex items-center justify-center gap-2"
                    title="Move category up"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                    Move Up
                  </button>
                  <button
                    onClick={() => handleMoveCategory(editingCategoryId, 'down')}
                    disabled={categories.findIndex(c => c._id === editingCategoryId) === categories.length - 1}
                    className="flex-1 px-3 py-2 bg-[rgba(243,235,226,0.06)] hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md transition-colors flex items-center justify-center gap-2"
                    title="Move category down"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                    Move Down
                  </button>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowCategoryModal(false);
                    setEditingCategoryId(null);
                    setNewCategoryData({ name: '', icon: '📦', featured: false });
                  }}
                  className="flex-1 px-4 py-2 bg-[rgba(243,235,226,0.06)] hover:bg-gray-600 text-white rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={editingCategoryId ? handleUpdateCategory : handleCreateCategory}
                  disabled={creatingCategory}
                  className="flex-1 px-4 py-2 bg-dusk-copper hover:bg-dusk-copper-deep text-white rounded-md transition-colors disabled:opacity-50"
                >
                  {creatingCategory ? (editingCategoryId ? 'Updating...' : 'Creating...') : (editingCategoryId ? 'Update' : 'Create')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrustedByTeams;
